// LR1 — THE LIVING ROOM'S SHARED PRIMITIVES.
//
// The identity chip, the @mention renderer, the @picker, and the types. Split out because all three
// of the room's surfaces (the feed, a thread, the inbox) render a member and a body, and the ONE
// rule this room cannot afford to get wrong twice is what a member's name and words look like.
//
// TWO LAWS LIVE IN THIS FILE:
//
//   1. **`author.name` IS THE ONLY NAME ANY SURFACE RENDERS.** The server built it with
//      `publicIdentity`, which has no field that could carry a surname or an email. `Who` below is
//      the only component that prints a member, so there is one place to check.
//
//   2. **A BODY IS RENDERED AS ELEMENTS, NEVER AS HTML.** `MentionBody` maps the shared law's parts
//      to React nodes. There is deliberately no `dangerouslySetInnerHTML` anywhere in this room:
//      highlighting an @tag by building an HTML string would put member-authored text into an HTML
//      sink, which is the one thing a community room must never do.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Crown, Play, Users } from "lucide-react";
import { useEscapeKey } from "./useEscapeKey";
import { downloadFromUrl } from "../lib/downloads";
import {
  renderMentionParts, activeMentionQuery, applyMention, EDITED_MARK, MENTION_PICKER_MAX,
  everyonePickerRow, EVERYONE_PICKER_HINT,
  followState, followLabel, followOnTap, FOLLOW_ACTION, UNFOLLOW_ACTION,
  MEMBER_MENU_FOLLOW, MEMBER_MENU_UNFOLLOW, MEMBER_MENU_MESSAGE,
  MEMBER_MENU_BLOCK, MEMBER_MENU_BLOCK_ASK, MEMBER_MENU_BLOCK_YES, MEMBER_MENU_BLOCK_NO, BLOCK_EVENT, canBlock,
  mediaPreloadFor,
} from "../livingRoom.js";

// LR1-12 ROUND EIGHT: no badge FIELD at all, and that is still true — nothing was added here. `owner`
// stays because her seat reads it for permissions (delete anything, see the queue).
// **ITEM 11 MAKES IT DRAW, AND THE TWO ORDERS DO NOT COLLIDE.** Round eight killed a general badge
// SYSTEM on identities; her crown is one mark, on her accounts alone, DERIVED from the boolean round
// eight deliberately kept rather than from a new field somebody has to set.
// ITEM 4(c): `photo` is a URL the SERVER built (`publicIdentity` → `avatarPhotoUrl`) and it is null
// for every member who has not put one there, which is the house glyph below. The client never
// composes this string: a photo the viewer may not see arrives as null, so there is no held state
// for a surface to leak and no second spelling of the route to drift.
// ITEM 7: `following` is a fact about the VIEWER, not about the member, and it rides here for the
// same reason the photo does — every surface renders a member through `Who`, so a fact carried on
// the identity arrives everywhere by existing.
// S-2: `crowned` is REQUIRED rather than optional, deliberately. An optional flag defaults to
// undefined, which is falsy, so a surface the server forgot to stamp would render no crown and
// nothing would be red — R27's live defect exactly (a capability behind an optional prop that
// neither call site passed). The type is what finds a forgotten one.
export type Identity = { ref: string; name: string; avatar: string; owner: boolean; crowned: boolean; photo?: string | null; following?: boolean };
// HER ORDER 2026-09-30: a post may carry ONE PDF or book (`document`), which the wire names and sizes.
export type Media = { id: string; kind: "image" | "video" | "document"; url: string; width?: number; height?: number; durationSeconds?: number; fileName?: string | null; sizeBytes?: number; contentType?: string };
export type SpellCounts = { members: number; print: number; journal: number; cauldron: number };
export type Spell = {
  id: string; postId: string; authorId: string; title: string;
  intention: string; materials: string; steps: string; notes: string;
  sourceKind: string; counts: SpellCounts;
};
export type Post = {
  id: string; title?: string | null; body: string; kind: string; author: Identity; authorRef: string; mine: boolean;
  likes: number; liked: boolean; comments: number; pinned: boolean; createdAt: string;
  editedAt?: string | null; draftBody?: string | null;
  mentions?: Record<string, string>; media?: Media[]; spell?: Spell | null; canComment?: boolean;
  // LR17-4 — **THIS RESULT MATCHED INSIDE A COMMENT.** Present only on a SEARCH answer, which is why
  // it is optional: it is a fact about why this row is on screen rather than a property of the post,
  // and it is what makes her landing clause possible (a post whose only match is in a comment lands
  // EXPANDED, or she is handed a title with no sign of the words she typed). A boolean about the
  // RESULT — never the comment, never its text, so nothing shadow-held can ride out on it.
  matchedComment?: boolean;
  // LR19 — **OWNER-ONLY FIELDS, AND OPTIONAL BECAUSE THAT IS LITERALLY TRUE ON THE WIRE.** For every
  // member the server sends `visibility: "public"` and omits `heldReason` entirely (`shapePost`), so
  // a member's payload cannot carry a hold even by accident. They are typed here so the owner's
  // treatment is type-checked rather than cast — and nothing may read them directly: the ONE reader
  // is `heldTreatment`, which asks the viewer first.
  visibility?: string;
  heldReason?: string | null;
};
// LR13 — a REPLY IS A COMMENT WITH A PARENT, which is why there is no `Reply` type here. `parentId`
// is null for a top-level comment; `replies` is filled by `nestComments` on the way to the screen and
// is never on the wire, because the ORDER the server sends is flat and oldest-first.
export type Comment = {
  id: string; body: string; author: Identity; authorRef?: string; mine: boolean;
  likes: number; liked: boolean; createdAt: string; parentId?: string | null;
  editedAt?: string | null; draftBody?: string | null; mentions?: Record<string, string>;
  replies?: Comment[]; media?: Media[];
  // TONIGHT-2 — **THIS REPLY HAS NOT LANDED YET**, and it is the same field `Message` carries for
  // the same reason (S1): her words are in the thread the instant she presses, pending until the
  // server confirms. CLIENT-ONLY — the server never sends it, and the answer replaces the whole
  // list, so a pending row exists only for the moment between the press and the answer. Optional,
  // so nothing on the wire has to know about it.
  pending?: boolean;
  // LR19 — owner-only, exactly as on `Post` and for the same reason. Her report was about a COMMENT.
  visibility?: string;
  heldReason?: string | null;
};
export type Message = {
  id: string; body: string; author: Identity; mine: boolean; likes: number; liked: boolean;
  createdAt: string; editedAt?: string | null; draftBody?: string | null; mentions?: Record<string, string>;
  // LR16: her pictures and her video. Never bytes — each carries the gated URL the one attachment
  // route serves, so the list and the bytes are decided by one predicate.
  media?: Media[];
  // LR16-e: SHE DELETED THIS. The row survives on purpose (LR1-16 keeps an honest tombstone rather
  // than rewriting what the others already read), so the thread needs to be TOLD, and `canDelete`
  // reads this exact field to stop offering a bin on something that is already gone.
  deleted?: boolean;
  // ITEM 10: WHO HAS READ THIS, and only ever for a message the viewer SENT. It is a list of refs
  // rather than timestamps on purpose — a per-participant read TIME on the wire is a per-thread
  // last-online, which her order forbids outright.
  seen?: string[];
  // S1 — **THIS ROW HAS NOT LANDED YET.** Her ruling: a message renders the instant she hits send,
  // "pending state until server-confirmed". It is CLIENT-ONLY and the server never sends it: a
  // pending row exists for the moment between the press and the answer, and the answer replaces the
  // whole thread. Optional, so nothing on the wire has to know about it.
  pending?: boolean;
  // HER QC (2026-08-07) — **THE SHARED POST THIS MESSAGE POINTS AT, ALREADY VIEWER-SCOPED BY THE
  // STORE.** Three states, not two (R18): ABSENT means this message is not a share and the ordinary
  // body renders; `gone: true` means it IS a share and there is nothing to show her (deleted, or a
  // post this reader may not see — deliberately the same answer, or the distinction would tell her a
  // hold exists); and a full card is a post she may read. Nothing on the client re-decides any of it.
  sharedPost?: SharedPostWire | null;
};
export type SharedPostWire = {
  id: string;
  gone?: boolean;
  title?: string;
  excerpt?: string;
  kind?: string;
  author?: Identity;
  // HER QC ROUND TWO — the picture area carries its KIND, because an image and a video are drawn
  // two different ways and round one drew both as an `<img>`. `null` means there is no picture area
  // at all, which is a text post's honest answer and not an empty box.
  thumb?: { url: string; kind: string } | null;
};
export type ThreadRow = {
  id: string; title: string; participants: Identity[]; group: boolean;
  // LR18b(b): whether the TITLE is one somebody typed or one the house composed from the people in
  // it. Her circle's medallion is the one surface where the difference shows.
  named: boolean;
  unread: number; preview: string; lastMessageAt: string;
  // ITEM 5: HER pin, on her own membership row — never the thread's, so it says nothing to the other
  // person in it.
  pinned?: boolean;
  // GROUP IMAGES: composed by the server from the stored version. `null` is no picture,
  // which is the default look by existing rather than by a flag.
  image?: string | null;
};
export type Notification = {
  id: string; kind: string; actor: Identity | null; text: string;
  target: { kind: string; id: string; commentId?: string | null; messageId?: string | null };
  read: boolean; at: string;
};
export type Guideline = { code: string; rule: string; body: string };
export type Me = {
  me: Identity; roomName: string; canPost: boolean; unread: number;
  silence: { until: string | null; notice: string } | null;
  limits: { titleChars: number; postChars: number; commentChars: number; messageChars: number; images: number; imageBytes: number; videoBytes: number; videoSeconds: number; documents: number; documentBytes: number };
  limitCopy: Record<string, string>;
  guidelines: { title: string; intro: string; rules: Guideline[] };
  spellActions: Array<{ key: string; label: string; verb: string }>;
  labels: { spellsSection: string; shareSpell: string };
  maxThreadMembers: number;
  // LR14: the panels she left open, on her ACCOUNT rather than this device.
  collapsedPosts?: string[];
  // COMMENTS COLLAPSE (her `5`): and which of those posts are showing their comments.
  openComments?: string[];
};

export const json = (url: string, init?: RequestInit) =>
  fetch(url, init).then((r) => r.json().catch(() => null)).catch(() => null);
export const postJson = (url: string, body: unknown) =>
  json(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export const ago = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return d < 7 ? `${d}d` : new Date(iso).toLocaleDateString();
};

// The house avatar set: twelve deterministic glyph tiles, drawn rather than uploaded. A member has a
// face on every post without a single uploaded image to moderate, and without a photograph becoming
// the identity leak the room-first-name law exists to prevent.
const AVATAR_GLYPH: Record<string, string> = {
  moon: "☾", sun: "☉", star: "✦", flame: "△", leaf: "❦", key: "⚷",
  eye: "◉", shell: "≈", crown: "♛", vessel: "⬡", thread: "⟡", bell: "◍",
};

// ITEM 4(c) — **THE ONE COMPONENT THAT DRAWS A MEMBER'S FACE**, which is why "the photo rides
// everywhere" was one component and not a sweep of nine surfaces. Every byline, every comment, every
// reply, every DM, the @picker and the notification list all render a member through `Who`, and
// `Who` renders this.
//
// The glyph is not a placeholder waiting to be replaced: it is the default, the fallback for a photo
// that fails to load, and where a member goes back to when she takes hers down or her seat hides one.
export function Avatar({ who, size = "md" }: { who: Identity; size?: "xs" | "sm" | "md" | "lg" }) {
  const [broken, setBroken] = useState(false);
  const photo = who.photo && !broken ? who.photo : null;
  return (
    <span className={`lr-avatar lr-avatar--${size}${who.owner ? " lr-avatar--owner" : ""}${photo ? " lr-avatar--photo" : ""}`} aria-hidden="true">
      {photo
        // A face that 404s must not leave a broken-image icon on somebody's post. `onError` falls
        // back to the glyph, which is the same thing that renders for everybody who never uploaded.
        ? <img className="lr-avatar__img" src={photo} alt="" loading="lazy" onError={() => setBroken(true)} />
        : AVATAR_GLYPH[who.avatar] || "✦"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ITEM 7 — **THE FOLLOW TAG, AND IT IS GRANTED PER SCREEN RATHER THAN PER CALL SITE.**
//
// Her order is "a FOLLOW tag beside EVERY @name EVERYWHERE it appears", from ONE shared component.
// The obvious shape — an `onFollow` prop on `Who` — is exactly R27's live defect: a capability that
// renders only where somebody remembered to pass a prop, and `TierBox.featured` was passed by
// NEITHER of its two call sites. So the tag asks a CONTEXT who the viewer is. A screen that says
// "this is a member surface and this is who is looking" gets the tag on every name it draws, in one
// line, and the surfaces where a follow control would be noise — her moderation seat, the @picker,
// the notification list — get nothing by saying nothing.
//
// **AND `self` IS WHY THE LAW RETURNS THREE STATES.** A boolean cannot say "draw no tag": every
// member sees her own name on her own posts, and a Follow button on it is a control that can only
// ever fail.
export const LivingRoomMe = createContext<string | null>(null);

// One member can be on the screen a dozen times (a post, four comments, the strip). The tag she taps
// is one of them, and the other eleven are stale the moment it lands — so the answer is broadcast.
// A window event rather than a store: this room's client is components and fetches, and a state
// container introduced for one boolean would be the second way state moves through it.
export const FOLLOW_EVENT = "12h:follow";

export function FollowTag({ who }: { who: Identity }) {
  const me = useContext(LivingRoomMe);
  const [following, setFollowing] = useState(Boolean(who.following));
  const [busy, setBusy] = useState(false);
  // The server's answer for THIS render wins over whatever the last tap left behind: a reload, a
  // refresh, or a different member arriving in the same slot.
  useEffect(() => { setFollowing(Boolean(who.following)); }, [who.ref, who.following]);
  useEffect(() => {
    const heard = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (String(detail.ref) === String(who.ref)) setFollowing(Boolean(detail.following));
    };
    window.addEventListener(FOLLOW_EVENT, heard);
    return () => window.removeEventListener(FOLLOW_EVENT, heard);
  }, [who.ref]);

  const state = followState({ ...who, following }, me);
  if (!state || state === "self") return null;
  const wanted = followOnTap(state);

  const tap = async () => {
    if (busy) return;
    setBusy(true);
    // Paint first — a tag that waits on a round trip reads as a control that did nothing — then
    // correct from the SERVER'S OWN ANSWER, never from what was asked for (R39: read the payload).
    setFollowing(Boolean(wanted));
    try {
      const r = await fetch("/api/living-room/follow", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: who.ref, following: wanted }),
      });
      const d = await r.json().catch(() => null);
      const truth = d?.ok ? Boolean(d.following) : Boolean(who.following);
      setFollowing(truth);
      rememberFollow(String(who.ref), truth);
      window.dispatchEvent(new CustomEvent(FOLLOW_EVENT, { detail: { ref: who.ref, following: truth } }));
    } catch {
      setFollowing(Boolean(who.following));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={state === "following" ? "lr-follow is-on" : "lr-follow"}
      onClick={(e) => { e.stopPropagation(); tap(); }}
      aria-pressed={state === "following"}
      // The FACE says what is; the accessible name says what tapping does. A button whose label and
      // whose state are the same word tells somebody who cannot see the colour nothing.
      aria-label={state === "following" ? UNFOLLOW_ACTION(who.name) : FOLLOW_ACTION(who.name)}
    >
      {followLabel(state)}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ITEM 11 — **THE CROWN.** Her accounts wear it beside @Lala everywhere.
//
// **IT RIDES THE `owner` FLAG AND NEVER THE NAME**, which is the whole safety of it. `owner` is set
// by `identityFrom(row, OWNER_EMAILS, viewer)` from the allowlist — the one truth — while ROOM NAMES
// ARE NOT UNIQUE in this room by LR1's own ruling (the fallback is a courtesy until a member claims
// a handle). A crown drawn on a name match would hand it to any member who calls herself Lala.
//
// **AND IT IS PART OF THE NAME, NOT A CONTROL BESIDE IT**, which is why it goes everywhere an @name
// is PRINTED rather than everywhere a member is DRAWN. ITEM 7's follow tag lives only inside `Who`
// because a tag is something to tap; the crown says WHO THIS IS, exactly as the `@` does, so it goes
// on the collapsed feed row too. (LR1-12 round eight cut that row to "title, @name, time, counts and
// NOTHING else" — the crown is spelling on the @name, not a something-else. Flagged for her word in
// RUNBOOK beside ITEM 7's, which is the same row and the opposite answer for the opposite reason.)
//
// **THE WORD IS AN `aria-label` ON A `role="img"`, NOT A `.visually-hidden` SPAN, AND THE WALKTHROUGH
// IS WHY.** The first version put the word in the DOM as clipped text — which announces correctly and
// CONCATENATES INTO THE NAME for anything that reads text: the walk's own extraction saw
// "@Lalathe house", and so does a member who selects a byline and copies it. `role="img"` with a
// label gives a screen reader the same sentence and leaves no text behind. A bare glyph read out as
// "black chess queen" is worse than nothing, and a `title` is not read at all on a phone.
// ---------------------------------------------------------------------------
export const CROWN_LABEL = "the house";

export function OwnerCrown({ who }: { who: Identity }) {
  // S-2 — **READS `crowned`, NEVER `owner`.** ITEM 11 derived this from the owner flag, which was
  // right while the two sets were the same and became a defect the moment they were not: her
  // daughters were ruled crowned and two of them hold no owner permission, so an `owner` read paints
  // the crown on exactly the wrong three accounts. The rosters are different sets by construction
  // (`CROWNED_EMAILS` is a superset of `OWNER_EMAILS`), and the fixture asserts that difference —
  // a check that cannot tell them apart would have passed on the old code too.
  if (!who?.crowned) return null;
  return (
    <span className="lr-crown" role="img" aria-label={CROWN_LABEL}>
      <Crown size={12} aria-hidden="true" />
    </span>
  );
}

// THE ONLY COMPONENT THAT PRINTS A MEMBER. `@Name` is the room's identity form (LR1-9), so the
// byline wears the @ too — one visual language for "who this is", whether it is a byline or a tag.
// ITEM 6 — **THE NAME TAP MENU, AND IT IS ONE COMPONENT EVERYWHERE.** Her order: the standalone
// follow buttons beside every name were clutter and come out; tapping any @name anywhere the house
// draws one opens a small menu with exactly two actions, Follow (showing Unfollow if she already
// follows) and Message.
//
// **IT PORTALS TO <body>, WHICH IS THE HALF THAT WOULD OTHERWISE COST A ROUND.** This room has hit
// the containing-block trap three times already (the N9-1 scrim, the LR1 composer picker, the header
// panel): `.nav` and several cards carry `backdrop-filter`/`transform`, and a filtered or transformed
// ancestor becomes the containing block for `position: fixed` descendants AND its own stacking
// context — so a menu rendered inline would be positioned against a POST CARD and clipped inside its
// overflow, with no z-index able to escape it. Portalling puts it on the viewport, where the
// coordinates it is given actually mean what they say.
//
// The follow ACTION is `FollowTag`'s own, unchanged and shared rather than reimplemented: paint
// first, then correct from the SERVER's answer, then broadcast on `FOLLOW_EVENT` so every other copy
// of that person on the screen moves with it (R28/R29 — one answer to what a follow does).
// The signal, not a prop. `Who` is drawn a dozen levels deep inside posts, comments, replies and
// rows; threading a handler down to every one of them is how a capability ends up living only on the
// surface that needed it first (R27's live defect, W12/R26/R35/R38's whole class). One event, one
// host, and a surface grants the menu by mounting the host once.
export const MEMBER_MENU_EVENT = "member-menu:open";

// **THE LATEST TRUTH ABOUT A FOLLOW, FOR A CONTROL THAT WAS NOT MOUNTED WHEN IT CHANGED.**
// `FollowTag` stays on screen and hears `FOLLOW_EVENT`, so it corrects itself. The menu does not: it
// opens, acts, and unmounts, so the NEXT time it opens it reads `who.following` off an identity the
// feed has not refetched — which showed "Follow" for somebody she had just followed, with Postgres
// disagreeing. Recording the last answer THE SERVER GAVE, keyed by ref, is what lets a fresh mount
// start from what is true rather than from what the payload was carrying when the page loaded.
// Deliberately not a second source of truth: it only ever holds a value the server returned, and the
// identity is still what it falls back to (R39 — read the payload, never what was asked for).
const followTruth = new Map<string, boolean>();
export function rememberFollow(ref: string, following: boolean) {
  followTruth.set(String(ref), Boolean(following));
}
export function knownFollow(who: Identity): boolean {
  const seen = followTruth.get(String(who.ref));
  return seen === undefined ? Boolean(who.following) : seen;
}

export function openMemberMenu(who: Identity, el: HTMLElement | null) {
  const r = el?.getBoundingClientRect();
  window.dispatchEvent(new CustomEvent(MEMBER_MENU_EVENT, {
    detail: { who, at: { x: Math.round(r?.left ?? 24), y: Math.round((r?.bottom ?? 80) + 6) } },
  }));
}

/** Mounted ONCE per surface. Everything else about the menu is `MemberMenu`'s. */
export function MemberMenuHost({ onMessage }: { onMessage: (ref: string) => void }) {
  const [open, setOpen] = useState<{ who: Identity; at: { x: number; y: number } } | null>(null);
  useEffect(() => {
    const heard = (e: Event) => setOpen((e as CustomEvent).detail || null);
    window.addEventListener(MEMBER_MENU_EVENT, heard);
    return () => window.removeEventListener(MEMBER_MENU_EVENT, heard);
  }, []);
  if (!open) return null;
  return <MemberMenu who={open.who} at={open.at} onClose={() => setOpen(null)} onMessage={onMessage} />;
}

export function MemberMenu({ who, at, onClose, onMessage }: {
  who: Identity; at: { x: number; y: number }; onClose: () => void; onMessage: (ref: string) => void;
}) {
  const me = useContext(LivingRoomMe);
  const [following, setFollowing] = useState(knownFollow(who));
  const [busy, setBusy] = useState(false);
  // I3: the block is asked once, then its answer is SAID in the menu (the server's own sentence, or its refusal).
  const [blockStep, setBlockStep] = useState<"" | "ask" | "said">("");
  const [blockSaid, setBlockSaid] = useState("");
  useEscapeKey(onClose);
  useEffect(() => {
    const heard = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (String(detail.ref) === String(who.ref)) setFollowing(Boolean(detail.following));
    };
    window.addEventListener(FOLLOW_EVENT, heard);
    return () => window.removeEventListener(FOLLOW_EVENT, heard);
  }, [who.ref]);

  const state = followState({ ...who, following }, me);
  // Her own name offers nothing: there is no following yourself and no messaging yourself.
  if (!state || state === "self") return null;
  const wanted = followOnTap(state);

  const follow = async () => {
    if (busy) return;
    setBusy(true);
    setFollowing(Boolean(wanted));
    try {
      const r = await fetch("/api/living-room/follow", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref: who.ref, following: wanted }),
      });
      const d = await r.json().catch(() => null);
      const truth = d?.ok ? Boolean(d.following) : Boolean(who.following);
      setFollowing(truth);
      rememberFollow(String(who.ref), truth);
      window.dispatchEvent(new CustomEvent(FOLLOW_EVENT, { detail: { ref: who.ref, following: truth } }));
    } catch { setFollowing(Boolean(who.following)); } finally { setBusy(false); }
  };

  const block = async () => {
    if (busy) return;
    setBusy(true);
    const d = await postJson("/api/living-room/block", { ref: who.ref, blocked: true });
    setBusy(false);
    setBlockSaid(String(d?.message || "That did not go through. Try again in a moment."));
    setBlockStep("said");
    if (d?.ok) window.dispatchEvent(new CustomEvent(BLOCK_EVENT, { detail: { ref: who.ref } }));
  };

  if (typeof document === "undefined") return null;
  // Kept inside the viewport: a menu opened from a name near the right edge would otherwise hang off
  // the screen, which is the same clipping in a different disguise.
  const left = Math.max(8, Math.min(at.x, (typeof window === "undefined" ? 400 : window.innerWidth) - 248));
  return createPortal(
    <div className="lr-membermenu__scrim" onMouseDown={onClose}>
      <div className="lr-membermenu" style={{ left, top: at.y }} role="menu"
        aria-label={`@${who.name}`} onMouseDown={(e) => e.stopPropagation()}>
        {/* ITEM 11 — the menu's own heading is a BYLINE, so it wears the crown like every other
            place this room prints a name. `lr15-crown.mjs` found it the round it shipped. */}
        <p className="lr-membermenu__who">@{who.name}<OwnerCrown who={who} /></p>
        <button type="button" className="lr-membermenu__item" role="menuitem" disabled={busy}
          onClick={() => { follow(); onClose(); }}>
          {state === "following" ? MEMBER_MENU_UNFOLLOW : MEMBER_MENU_FOLLOW}
        </button>
        <button type="button" className="lr-membermenu__item" role="menuitem"
          onClick={() => { onMessage(String(who.ref)); onClose(); }}>
          {MEMBER_MENU_MESSAGE}
        </button>
        {canBlock(who) && blockStep === "" ? (
          <button type="button" className="lr-membermenu__item lr-membermenu__item--block" role="menuitem"
            onClick={() => setBlockStep("ask")}>
            {MEMBER_MENU_BLOCK}
          </button>
        ) : null}
        {blockStep === "ask" ? (
          <div className="lr-membermenu__ask">
            <p>Block @{who.name}<OwnerCrown who={who} />? {MEMBER_MENU_BLOCK_ASK}</p>
            <div className="lr-membermenu__askrow">
              <button type="button" className="lr-membermenu__item" onClick={() => setBlockStep("")}>{MEMBER_MENU_BLOCK_NO}</button>
              <button type="button" className="lr-membermenu__item lr-membermenu__item--block" disabled={busy} onClick={block}>{MEMBER_MENU_BLOCK_YES}</button>
            </div>
          </div>
        ) : null}
        {blockStep === "said" ? <p className="lr-membermenu__said" role="status">{blockSaid}</p> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Who({ who, at, edited, size = "md", onOpen }: { who: Identity; at?: string; edited?: boolean; size?: "sm" | "md"; onOpen?: () => void }) {
  return (
    <span className="lr-who">
      {/* >>> HER ITEM 7: THE FACE OPENS THE SAME BOX THE NAME DOES. <<< The name has been a door
          since ITEM 6 and the avatar beside it never was, so the most obvious profile tap on the
          site -- somebody's picture -- did nothing, and which half of one identity you happened to
          hit decided whether anything happened at all.

          It routes through `openMemberMenu` rather than growing its own handler, so Follow, Message
          and her block door all reach the avatar by EXISTING (R28/R29) and there is one answer to
          what tapping a person does. Where the name is plain text -- her own seat, the picker -- the
          face stays plain text with it: a door that opens onto nobody is the dead tap this is about.

          A BUTTON rather than a click handler on the image, so it is reachable by keyboard and
          announces itself, and the label names the person because the picture cannot. */}
      {onOpen ? (
        <button type="button" className="lr-who__face" aria-label={`@${who.name}`}
          onClick={(e) => openMemberMenu(who, e.currentTarget)}>
          <Avatar who={who} size={size} />
        </button>
      ) : (
        <Avatar who={who} size={size} />
      )}
      {/* LR1 ROUND FIVE — **THE NAME OPENS THE DOOR.** Every @name in the room is a button that opens
          a conversation with that member. When no handler is given (her seat, the picker) it renders
          as plain text rather than a dead button. */}
      {/* ITEM 6: the name opens the MENU now rather than jumping straight into a conversation. The
          coordinates are the tap's own, so the menu appears where her thumb is. Message is still one
          tap from here, it simply is not the only thing the name can do. */}
      {onOpen ? (
        <button type="button" className="lr-who__name lr-who__name--door"
          onClick={(e) => openMemberMenu(who, e.currentTarget)}>
          <strong>@{who.name}</strong><OwnerCrown who={who} />
        </button>
      ) : (
      <span className="lr-who__name">
        <strong>@{who.name}</strong><OwnerCrown who={who} />
      </span>
      )}
      {/* ITEM 6 — **THE STANDALONE FOLLOW TAG IS GONE FROM BESIDE THE NAME** (her order: it looked
          cluttered). Follow now lives inside the menu the NAME opens, which is one control instead of
          two and the same two actions everywhere the house draws a member. `FollowTag` itself is
          kept — the circle lists still use it, where a row of people IS the surface and a tap-target
          per row is the point. */}
      {at ? <span className="lr-when">{ago(at)}</span> : null}
      {/* LR1-16: the marker says an edit happened and nothing about what changed. No public history. */}
      {edited ? <span className="lr-edited">{EDITED_MARK}</span> : null}
    </span>
  );
}

// A body with its @tags styled. `mentions` maps the lowercased typed name to the member ref it was
// resolved to AT WRITE TIME, which is why a rename never re-points an old tag at somebody else.
export function MentionBody({ text, mentions, className }: { text: string; mentions?: Record<string, string>; className?: string }) {
  const parts = renderMentionParts(text, mentions || {}) as Array<{ type: string; text: string; ref?: string; href?: string }>;
  return (
    <p className={className}>
      {parts.map((p, i) => {
        // HER RULE 4: the house-wide call reads visibly special — gold — so a member knows this one
        // is for the whole house rather than for her by name.
        if (p.type === "everyone") return <em key={i} className="lr-tag lr-tag--everyone">{p.text}</em>;
        if (p.type === "mention") return <em key={i} className="lr-tag">{p.text}</em>;
        // C5: **A REAL ANCHOR, AND IT OPENS AWAY FROM THE HOUSE.** `target="_blank"` is her clause;
        // `noopener` is what stops the page she opened from reaching back through `window.opener` and
        // navigating the tab she left the room in, which is the one way a link a MEMBER pasted can
        // cost another member her session. `noreferrer` is this house's own existing convention on
        // every other anchor it draws, and `nofollow ugc` is the standard for text somebody else
        // wrote — this house does not vouch for wherever a post points (DECISIONS C5-2).
        if (p.type === "link" && p.href) {
          return <a key={i} className="lr-link" href={p.href} target="_blank" rel="noopener noreferrer nofollow ugc">{p.text}</a>;
        }
        return <span key={i}>{p.text}</span>;
      })}
    </p>
  );
}

// ---------------------------------------------------------------------------
// LR1-9 — THE @ PICKER. Attaches to any text input or textarea.
//
// The candidate list comes from the SERVER, which builds it from participation. This component never
// filters a member list of its own, because it never receives one: there is no client-side directory
// to leak, which is the same boundary the server's query enforces, kept on this side too.
// ---------------------------------------------------------------------------
export function useMentionPicker(threadId?: string | null, isOwner = false) {
  const [people, setPeople] = useState<Identity[]>([]);
  const [active, setActive] = useState<{ query: string; start: number; end: number } | null>(null);
  const timer = useRef<number | null>(null);

  const look = useCallback((text: string, caret: number) => {
    const found = activeMentionQuery(text, caret) as { query: string; start: number; end: number } | null;
    setActive(found);
    if (!found) { setPeople([]); return; }
    if (timer.current) window.clearTimeout(timer.current);
    // Debounced: a picker that fires a request per keystroke is a request per keystroke.
    timer.current = window.setTimeout(async () => {
      // HER DEFECT (a): nothing is asked for until at least one letter is typed, which
      // `activeMentionQuery` now enforces, so a bare @ never opens the roster.
      const qs = new URLSearchParams({ q: found.query });
      if (threadId) qs.set("threadId", threadId);
      const d = await json(`/api/living-room/mentions?${qs.toString()}`);
      const members = d?.ok ? (d.people || []).slice(0, MENTION_PICKER_MAX) : [];
      // **EVERYONE LEADS THE LIST, FOR AN OWNER ONLY.** Her rule 1: a regular member's picker never
      // contains it, so there is no row to tap and no refusal to meet. The law decides, not this
      // component (R15), and it matches on what she has typed so far so `@ev` still finds it.
      const house = everyonePickerRow({ isOwner, query: found.query || "" }) as (Identity & { everyone?: boolean }) | null;
      setPeople(house ? [house as Identity, ...members] : members);
    }, 140);
  }, [threadId]);

  const choose = useCallback((text: string, name: string) => {
    const next = applyMention(text, active, name) as { text: string; caret: number };
    setActive(null);
    setPeople([]);
    return next;
  }, [active]);

  const dismiss = useCallback(() => { setActive(null); setPeople([]); }, []);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  return { people: active ? people : [], active, look, choose, dismiss };
}

// P3-1 — **THE PICKER IS PORTALED TO `<body>`, AND THE CAUSE WAS NEVER THE Z-INDEX.**
//
// Her diagnostic named the surface exactly: the picker renders correctly in the NEW POST composer and
// hides behind the panel in the inline COMMENT/REPLY compose. The reason those two differ is not
// stacking order, it is **`overflow: hidden` on `.lr-post`** — round eight put it there to keep the
// unfold inside the card's rounded corners, which is right, and an absolutely-positioned child of a
// clipped ancestor is CLIPPED no matter how high its z-index goes. The post composer has no such
// ancestor, so the same component looked fine there. That is the containing-block family's fifth
// appearance and the reason a z-index fix "didn't reach every surface": it was never the fix.
//
// A portal leaves the ancestor entirely, so there is nothing left to clip it and nothing left to
// stack it under — and it takes the `--z-header-panel` family token, which is what every other
// escaping layer in this house already uses.
//
// **THE ANCHOR IS REQUIRED, not optional.** Leaving the flow means the picker no longer knows where
// its box is, and an optional anchor is exactly R27's shape: the one call site that forgot it would
// render a correct list in the wrong place, which is worse than the bug being fixed.
export function MentionPicker({
  people, onPick, anchor,
}: { people: Identity[]; onPick: (name: string) => void; anchor: RefObject<HTMLElement | null> }) {
  const [box, setBox] = useState<{ left: number; top: number; width: number; above: boolean } | null>(null);

  useEffect(() => {
    if (!people.length) { setBox(null); return; }
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // FLIP ABOVE when there is not room beneath — on a phone the comment box sits near the bottom
      // of the screen with the keyboard under it, which is precisely where a list drawn downward is
      // a list she cannot see.
      const room = window.innerHeight - r.bottom;
      setBox({ left: r.left, top: r.bottom, width: r.width, above: room < 200 });
    };
    place();
    // The page can move under her while the list is open (an image loading, the keyboard arriving).
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [people.length, anchor]);

  if (!people.length || !box) return null;
  const style: CSSProperties = box.above
    ? { left: box.left, bottom: window.innerHeight - box.top + 6, width: box.width }
    : { left: box.left, top: box.top + 6, width: box.width };
  return createPortal(
    <ul className="lr-picker lr-picker--portal" role="listbox" aria-label="Tag a member" style={style}>
      {people.map((p) => {
        // **THE HOUSE-WIDE CALL LEADS THE LIST AND LOOKS LIKE WHAT IT IS.** Gold, with a plain line
        // saying what tapping it does — she should never have to guess that this one reaches
        // everybody. Tapping inserts the connected tag exactly as tapping a member does.
        const house = (p as Identity & { everyone?: boolean }).everyone === true;
        return (
          <li key={p.ref}>
            <button type="button" className={house ? "lr-picker__everyone" : undefined}
              onMouseDown={(e) => { e.preventDefault(); onPick(house ? "everyone" : p.name); }}>
              {house
                ? <span className="lr-picker__housemark" aria-hidden="true"><Users size={16} /></span>
                : <Avatar who={p} size="sm" />}
              <span>@{house ? "everyone" : p.name}{house ? null : <OwnerCrown who={p} />}
                {house ? <em className="lr-picker__hint">{EVERYONE_PICKER_HINT}</em> : null}</span>
            </button>
          </li>
        );
      })}
    </ul>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// LR16 — **THE MEDIA UNDER A MESSAGE.** Her order: images render inline at a clean thumbnail scale
// and a tap opens the round-nine lightbox.
//
// It is hookless and it takes an `onOpen`, so a gate CALLS it and reads the tree back — which is the
// only way the laws in it can be asked anything.
//
// **ITEM 7 (2026-08-11) SUPERSEDES LR16's INLINE-VIDEO RULING AT ITS OWN DECLARATION** (DL-2/R181:
// an override written below leaves the retired law standing beside its replacement and the next
// reader cannot tell which one draws). LR16 said *"video plays full with controls"* and this comment
// used to say a video is not a lightbox item, because `ImageViewer` drew an `<img>` and nothing
// else. **Her report is that exact behaviour**: *"a video plays inline inside the tiny bubble and
// going big needs an expander nobody sees."* Her ruling — inline it is a THUMBNAIL WITH A PLAY
// BADGE, **never auto-playing small**, and a tap opens the SAME full-screen popup pictures get.
//
// So the video carries NO `controls` and the button around it is the tap target, exactly as a
// picture's is. **The frame she sees is the file's own first frame** — a `<video preload="metadata">`
// paints it with no poster stored anywhere (the shared-card round already proved this), so her
// "video cover" clause needs no migration, no job and no second set of bytes.
//
// **AND THE INDEX HANDED OVER IS NOW THE INDEX WITHIN THE WHOLE STRIP**, because the viewer takes
// the whole strip. The old arithmetic — an index within a filtered IMAGES list — is exactly what
// went wrong silently when a video sat first in a message, and it is deleted rather than adjusted.
// IT #124 (2026-08-21) — **ITEM 7's FIRST-FRAME THUMBNAIL IS WHAT `metadata` IS FOR, AND IT IS
// UNCONDITIONAL NO LONGER.** In Elite 2026 nine video rows sit 38 to 497 rows above the fold, with
// 263.6MB behind them and the biggest at 62.5MB, and all nine fetched the moment she opened the
// thread — against the FIVE-CONNECTION POOL PERF-1 measured saturating on exactly this class, on the
// surface that round did not touch. **A row past the window draws its play badge over an empty frame
// and still opens the full player on a tap**, and that cost is named rather than hidden (R17/R160).
//
// IT #124 — **`eager` DEFAULTS TO TRUE, SO EVERY EXISTING CALLER IS BYTE-IDENTICAL** (ITEM 12's
// neutrality argument). Only the inbox thread, where nine offscreen videos really do fetch on open,
// hands over a `false`; the decision itself is `mediaPreloadFor` in `src/livingRoom.js`, where a
// gate RUNS it rather than grepping a ternary inside a memoized component.
export function MessageMedia({
  media, onOpen, eager = true,
}: { media: Media[]; onOpen?: (index: number) => void; eager?: boolean }) {
  const items = Array.isArray(media) ? media : [];
  if (!items.length) return null;
  return (
    <div className={`lr-msg__media lr-msg__media--${Math.min(items.length, 4)}`}>
      {items.map((m, i) => (m.kind === "video" ? (
        // `muted` and no `controls` together are what make "never auto-playing small" structural
        // rather than a promise: there is nothing on this element for a thumb to start, and
        // `playsInline` keeps an iPhone from taking the whole screen if anything ever does.
        <button key={m.id} type="button" className="lr-msg__open lr-msg__open--video"
          aria-label="Open this video" onClick={() => onOpen?.(i)}>
          <video className="lr-msg__video" src={m.url} playsInline muted preload={mediaPreloadFor(eager)} />
          <span className="lr-msg__play" aria-hidden="true"><Play size={22} /></span>
        </button>
      ) : (
        <button key={m.id} type="button" className="lr-msg__open" aria-label="Open this picture"
          onClick={() => onOpen?.(i)}>
          <img className="lr-msg__img" src={m.url} alt="" loading="lazy" />
        </button>
      )))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ITEM 4(b) — FULL SIZE, WITH DOWNLOAD. **ONE VIEWER, AND EVERY PICTURE IN THIS ROOM OPENS IN IT.**
//
// Her order names it for comment images. Measured first: this room had NO full-size view at all —
// a post's pictures were `object-fit: cover` thumbnails and that was the end of them, so a member
// who wanted to read the label on a bottle in somebody's altar photo could not. Building it only for
// comments would have been the exact shape this house keeps paying for (W12, R26, R35): the
// capability living on whichever surface needed it first. It takes a list and an index, so a post
// with four pictures pages through them.
//
// It portals to `<body>` for the same reason the @picker does (P3-1): `.lr-post` carries
// `overflow: hidden`, and an absolutely-positioned child of a clipped ancestor is clipped however
// high its z-index climbs.
export function ImageViewer({
  items, index, onClose,
}: { items: Media[]; index: number; onClose: () => void }) {
  const [at, setAt] = useState(index);
  const swipeFrom = useRef<number | null>(null);
  const shown = items[Math.max(0, Math.min(at, items.length - 1))];
  // R82: the walk between pictures is this viewer's own; the CLOSE is the house's one law.
  useEscapeKey(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setAt((n) => Math.min(n + 1, items.length - 1));
      if (e.key === "ArrowLeft") setAt((n) => Math.max(n - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length]);
  if (!shown) return null;
  return createPortal(
    <div className="lr-viewer" role="dialog" aria-modal="true"
      aria-label={shown.kind === "video" ? "Video" : "Picture"} onClick={onClose}>
      <button type="button" className="lr-viewer__close" aria-label="Close" onClick={onClose}>x</button>
      {/* The picture and its controls stop the click, so tapping the backdrop closes and tapping the
          photograph does not. */}
      <div className="lr-viewer__stage" onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { swipeFrom.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          // LR16 — **SWIPE BETWEEN, her word, and it is added HERE rather than in the inbox** so the
          // posts and the comments that have used this viewer since ITEM 4(b) get it in the same
          // edit. A gesture built for one caller is exactly the shape this house keeps paying for
          // (W12/R26/R35: the capability living on whichever surface needed it first).
          //
          // 40px, because a tap on a phone drifts a few pixels and a drift is not a swipe — under
          // that the gesture does nothing and the picture stays where it is, which is the direction
          // with no cost. Pinch-zoom is the BROWSER'S, and deliberately not re-implemented: the
          // viewer sets no `touch-action` and no `user-scalable`, so the platform gesture is what
          // she gets, on the medium that has always done it best.
          const from = swipeFrom.current;
          swipeFrom.current = null;
          const to = e.changedTouches[0]?.clientX;
          if (from === null || to === undefined || Math.abs(to - from) < 40) return;
          if (to < from) setAt((n) => Math.min(n + 1, items.length - 1));
          else setAt((n) => Math.max(n - 1, 0));
        }}>
        {/* ITEM 7 — **THE VIEWER LEARNED TO DRAW A VIDEO, AND THE FIX BELONGS HERE.** Her clause is
            about messages, but the inbox is not where this could be repaired: posts and comments
            hand this same viewer the same strips, and a capability living on whichever surface
            needed it first is the shape this house keeps paying for (W12/R26/R35). One edit, three
            call sites, and the paging, the swipe, the arrow keys and Escape are already this
            component's.

            `controls` and NOT autoplay, which is her *"showing it large with a Play button"* read
            literally — a lightbox that starts playing the moment it opens is the small-video
            complaint again at full size. `object-fit: contain` in the sheet is the uncropped half. */}
        {shown.kind === "video"
          ? <video className="lr-viewer__video" src={shown.url} controls playsInline preload="metadata" />
          : <img className="lr-viewer__img" src={shown.url} alt="" />}
        <div className="lr-viewer__bar">
          {items.length > 1 ? (
            <>
              <button type="button" className="lr-viewer__step" aria-label="Previous picture"
                disabled={at <= 0} onClick={() => setAt((n) => Math.max(n - 1, 0))}>&#8249;</button>
              <span className="lr-viewer__count">{at + 1} / {items.length}</span>
              <button type="button" className="lr-viewer__step" aria-label="Next picture"
                disabled={at >= items.length - 1} onClick={() => setAt((n) => Math.min(n + 1, items.length - 1))}>&#8250;</button>
            </>
          ) : null}
          {/* DOWNLOAD, her word. `download` on a same-origin href is the whole mechanism — the bytes
              are already served by our own route, so nothing here needs a second copy of the URL. */}
          {/* IT #391: the same repair as the document card, through the house's one transport. */}
          <button type="button" className="lr-viewer__download" onClick={() => { void downloadFromUrl(shown.url); }}
            aria-label={shown.kind === "video" ? "Download this video" : "Download this picture"}>Download</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
